import { Injectable } from '@angular/core';
import { GridOptions } from 'ag-grid-community';
import { CustomActionCellRendererComponent } from '../custom-action-cell-renderer/custom-action-cell-renderer.component';
import { ResourceNameCellRendererComponent } from '../resource-name-cell-renderer/resource-name-cell-renderer.component';
import { CopyCellRendererComponent } from '../copy-cell-renderer/copy-cell-renderer.component';
import { StatusLabelComponent } from '@app/shared/components/Status-Label/status-label.component';

export interface GridConfig {
  enablePagination: boolean;
  paginationPageSize: number;
  enableRowSelection: boolean;
  rowSelectionMode: 'single' | 'multiple';
  enableSorting: boolean;
  enableFiltering: boolean;
  enableColumnResize: boolean;
  showToolPanel: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class GridConfigService {
  createGridOptions(config: GridConfig, callbacks: any): GridOptions {
    return {
      pagination: config.enablePagination,
      paginationPageSize: config.paginationPageSize,
      paginationPageSizeSelector: config.enablePagination
        ? [10, 20, 50, 100]
        : false,

      rowSelection: this.getRowSelectionConfig(config),
      suppressRowClickSelection: false,
      suppressRowDeselection: false,
      enableRangeSelection: false,
      suppressCellFocus: true,
      suppressRowHoverHighlight: false,

      // Configure selection column properties
      ...(config.enableRowSelection &&
        config.rowSelectionMode === 'multiple' && {
          selectionColumnDef: {
            sortable: false,
            resizable: false,
            width: 50,
            minWidth: 50,
            maxWidth: 50,
            suppressHeaderMenuButton: true,
            pinned: 'left',
            lockPosition: 'left',
            suppressMovable: true,
          },
        }),

      components: {
        ResourceNameCellRenderer: ResourceNameCellRendererComponent,
        CustomActionCellRenderer: CustomActionCellRendererComponent,
        StatusLabelCellRenderer: StatusLabelComponent,
        CopyCellRenderer: CopyCellRendererComponent,
      },

      context: {
        onResourceClick: callbacks.onResourceClick,
      },

      defaultColDef: {
        sortable: config.enableSorting,
        filter: config.enableFiltering,
        resizable: config.enableColumnResize,
        minWidth: 80,
        autoHeaderHeight: true,
        wrapText: true,
        autoHeight: true,
      },

      autoSizeStrategy: {
        type: 'fitCellContents',
        defaultMinWidth: 80,
        defaultMaxWidth: 400,
      },

      suppressColumnVirtualisation: false,
      sortingOrder: config.enableSorting ? ['asc', 'desc'] : undefined,
      animateRows: false,
      rowHeight: 64,
      headerHeight: 56,

      sideBar: config.showToolPanel ? this.createSideBar() : false,

      onGridReady: callbacks.onGridReady,
      onSelectionChanged: callbacks.onSelectionChanged,
      onCellClicked: callbacks.onCellClicked,
      onCellDoubleClicked: callbacks.onCellDoubleClicked,
      onSortChanged: callbacks.onSortChanged,
      onFilterChanged: callbacks.onFilterChanged,
      onColumnMoved: callbacks.onColumnMoved,
    };
  }

  private getRowSelectionConfig(config: GridConfig) {
    if (!config.enableRowSelection) return undefined;

    return config.rowSelectionMode === 'single'
      ? { mode: 'singleRow' as const, checkboxes: false, headerCheckbox: false }
      : {
          mode: 'multiRow' as const,
          checkboxes: true,
          headerCheckbox: true,
        };
  }

  private createSideBar() {
    return {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
        },
      ],
    };
  }
}
